import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { Election, Candidate } from '../types';
import toast from 'react-hot-toast';

export const useElections = (nodeId: string, filterType?: 'ELECTION' | 'POLL') => {
  return useQuery<Election[]>({
    queryKey: ['elections', nodeId, filterType],
    queryFn: async () => {
      if (!nodeId) return [];
      const response = await apiWorker.get(`/api/elections`, { params: { nodeId, ...(filterType ? { type: filterType } : {}) } });
      const items: Election[] = (response.data && Array.isArray(response.data.content)) ? response.data.content : (Array.isArray(response.data) ? response.data : []);
      if (!filterType) return items;
      return items.filter((item: any) => {
        const itemType = item.type || (Array.isArray(item.options) && item.options.length > 0 ? 'POLL' : 'ELECTION');
        return itemType === filterType;
      });
    },
    enabled: !!nodeId,
  });
};

export const useCandidates = (electionId: string) => {
  return useQuery<Candidate[]>({
    queryKey: ['candidates', electionId],
    queryFn: async () => {
      if (!electionId) return [];
      const response = await apiWorker.get(`/api/elections/${electionId}/candidates`);
      return (response.data && Array.isArray(response.data.content)) ? response.data.content : response.data;
    },
    enabled: !!electionId,
  });
};

export const useVote = (electionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (candidateId: string) => 
      apiWorker.post(`/api/elections/${electionId}/vote`, null, { params: { candidateId } }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates', electionId] });
      queryClient.invalidateQueries({ queryKey: ['elections'] });
      toast.success('Vote submitted successfully!');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to submit vote';
      toast.error(message);
    }
  });
};

export const useCreateElection = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Election> & { options?: string[] }) => 
      apiWorker.post(`/api/elections`, data, { params: { nodeId } }).then(r => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['elections'] });
      toast.success('Election process initialized successfully!', { icon: '🗳️' });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to initialize election';
      toast.error(message);
    }
  });
};

export const useNominate = (electionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<Candidate>) => 
      apiWorker.post(`/api/elections/${electionId}/nominate`, data).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['candidates', electionId] });
      toast.success('Nomination submitted successfully!');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to submit nomination';
      toast.error(message);
    }
  });
};
